import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createContactRequest,
  getContacts,
  getSubjects,
  removeContact,
  updateContactRequest,
} from "../../../../services/api/landing/contactUs/contactUsApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Contact created successfully!",
    ar: "تم إنشاء جهة الاتصال بنجاح!",
  },
  addError: {
    en: "Failed to create contact.",
    ar: "فشل في إنشاء جهة الاتصال.",
  },
  update: {
    en: "Contact updated successfully!",
    ar: "تم تحديث جهة الاتصال بنجاح!",
  },
  updateError: {
    en: "Failed to update contact.",
    ar: "فشل في تحديث جهة الاتصال.",
  },
  delete: {
    en: "Contact deleted successfully!",
    ar: "تم حذف جهة الاتصال بنجاح!",
  },
  deleteError: {
    en: "Failed to delete contact.",
    ar: "فشل في حذف جهة الاتصال.",
  },
};

export const fetchContacts = createAsyncThunk(
  "contacts/fetchContacts",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getContacts(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch contacts");
    }
  },
);

export const createContact = createAsyncThunk(
  "contacts/createContact",
  async (contactData, { rejectWithValue }) => {
    try {
      const response = await createContactRequest(contactData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create contact");
    }
  },
);

export const updateContact = createAsyncThunk(
  "contacts/updateContact",
  async ({ id, contactData }, { rejectWithValue }) => {
    try {
      const response = await updateContactRequest(id, contactData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update contact");
    }
  },
);

export const deleteContact = createAsyncThunk(
  "contacts/deleteContact",
  async (contactId, { rejectWithValue }) => {
    try {
      await removeContact(contactId);
      return contactId;
    } catch (error) {
      return rejectWithValue("Failed to delete contact");
    }
  },
);

export const fetchSubjects = createAsyncThunk(
  "contacts/fetchSubjects",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSubjects();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch subjects");
    }
  },
);

const contactUsSlice = createSlice({
  name: "contacts",
  initialState: {
    contactData: [],
    subjects: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contactData = action.payload;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.contactData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createContact.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        const index = state.contactData.findIndex(
          (contact) => contact.id === action.payload.id,
        );
        if (index !== -1) {
          state.contactData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.contactData = state.contactData.filter(
          (contact) => contact.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      })
      .addCase(fetchSubjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubjects.fulfilled, (state, action) => {
        state.loading = false;
        state.subjects = action.payload;
      })
      .addCase(fetchSubjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default contactUsSlice.reducer;
